/**
 * ============================================================================
 * RESIN DIY KIT CALCULATOR — UK
 * ============================================================================
 * Pairs with sections/resin-kit-calculator.liquid.
 *
 * Completely independent of the live binder calculator
 * (vuba-selection-calculator.js). Different file, different selectors, its own
 * IIFE — it cannot affect /pages/online-calculator.
 *
 * Everything is scoped to the section element, so more than one instance can
 * live on a page without clashing.
 *
 * How products are resolved
 * -------------------------
 *  - Kit colours are read LIVE from the collection (default: easihold-pro-kits)
 *    via /collections/<handle>/products.json. Add a new colour kit to that
 *    collection and it shows up here on its own — no code change.
 *    NOTE: handles are the source of truth, never titles. "Nero" currently has
 *    the handle easihold-resin-bound-gravel-kit-1m2-apollo-grey.
 *  - Mesh / Crushed Glass / Trowel are resolved from their handle via
 *    /products/<handle>.js, which also gives market-correct prices.
 *  - No variant IDs are hard-coded anywhere, so nothing breaks when the client
 *    edits or re-creates a product.
 * ============================================================================
 */

(function () {
    'use strict';

    /* ---------------------------------------------------------------- utils */

    var PRODUCT_CACHE = {};

    function fetchProduct(handle) {
        if (!handle) return Promise.resolve(null);
        if (PRODUCT_CACHE[handle]) return Promise.resolve(PRODUCT_CACHE[handle]);
        return fetch('/products/' + handle + '.js')
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (d) { if (d) PRODUCT_CACHE[handle] = d; return d; })
            .catch(function () { return null; });
    }

    function fetchCollection(handle) {
        if (!handle) return Promise.resolve([]);
        return fetch('/collections/' + handle + '/products.json?limit=250')
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (d) { return (d && d.products) || []; })
            .catch(function () { return []; });
    }

    function activeCurrency() {
        if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
            return window.Shopify.currency.active;
        }
        return 'GBP';
    }

    // amount is in cents (Shopify Ajax API format)
    function money(cents) {
        if (typeof cents !== 'number' || isNaN(cents)) return '';
        try {
            return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
                style: 'currency',
                currency: activeCurrency()
            }).format(cents / 100);
        } catch (e) {
            return (cents / 100).toFixed(2);
        }
    }

    function escapeHtml(str) {
        return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
            return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
    }

    // Strip the boilerplate off a kit title so the swatch label stays short.
    // "Easihold Resin Bound Gravel Kit 1m2 - Coshel Quartz" -> "Coshel Quartz"
    function shortColourName(title) {
        var t = String(title || '');
        var dash = t.lastIndexOf(' - ');
        if (dash > -1) return t.slice(dash + 3).trim();
        return t.replace(/^Easihold\s+/i, '').trim();
    }

    /* -------------------------------------------------------------- instance */

    function initSection(root) {
        var sectionId = root.getAttribute('data-rkc-section');
        var settings = (window.RKC_SETTINGS && window.RKC_SETTINGS[sectionId]) || {};

        var form = root.querySelector('[data-rkc-form]');
        if (!form) return;

        var progress = root.querySelector('[data-rkc-progress]');
        var coloursWrap = root.querySelector('[data-rkc-colours]');
        var resultsWrap = root.querySelector('[data-rkc-results]');
        var cartBtn = root.querySelector('[data-rkc-cart]');
        var restartBtn = root.querySelector('[data-rkc-restart]');
        var areaInput = root.querySelector('[data-rkc-area]');
        var vubaLink = root.querySelector('[data-rkc-vuba-link]');

        var allSteps = Array.prototype.slice.call(root.querySelectorAll('[data-rkc-step]'));
        // The ordered list of question steps. Steps the merchant switched off in
        // the theme editor are simply not rendered, so the flow adapts itself.
        var flow = allSteps
            .filter(function (s) { return s.getAttribute('data-rkc-question') === 'true'; })
            .map(function (s) { return s.getAttribute('data-rkc-step'); });

        var coveragePerKit = Number(settings.coveragePerKit) > 0 ? Number(settings.coveragePerKit) : 1;
        var maxArea = Number(settings.maxArea) || 0;
        var showPrices = settings.showPrices !== false;

        var state = { area: 0, kitQty: 0, colour: null, mesh: 'no', glass: 'no', trowel: 'no' };
        var current = flow[0];
        var lineItems = [];

        if (vubaLink && (!settings.vubaUrl || settings.vubaUrl === '#')) {
            // No URL supplied yet — don't ship a dead link.
            vubaLink.setAttribute('aria-disabled', 'true');
            vubaLink.style.opacity = '0.55';
            vubaLink.style.pointerEvents = 'none';
        }

        /* ---------------------------------------------------- step navigation */

        function stepEl(name) {
            return root.querySelector('[data-rkc-step="' + name + '"]');
        }

        function goTo(name) {
            var target = stepEl(name);
            if (!target) return;
            allSteps.forEach(function (s) { s.classList.remove('vsc-step--active'); });
            target.classList.add('vsc-step--active');
            current = name;

            var idx = flow.indexOf(name);
            if (progress) {
                progress.style.width = (idx === -1 ? 100 : ((idx + 1) / flow.length) * 100) + '%';
            }

            var counter = target.querySelector('[data-rkc-counter]');
            if (counter && idx > -1) {
                counter.textContent = 'Step ' + (idx + 1) + ' of ' + flow.length;
            }

            var top = root.getBoundingClientRect().top + window.pageYOffset - 20;
            window.scrollTo({ top: top, behavior: 'smooth' });
        }

        function showError(step, message) {
            var el = root.querySelector('[data-rkc-error="' + step + '"]');
            if (!el) { window.alert(message); return; }
            el.textContent = message;
            el.classList.add('vscx-error--show');
        }

        function clearError(step) {
            var el = root.querySelector('[data-rkc-error="' + step + '"]');
            if (el) { el.textContent = ''; el.classList.remove('vscx-error--show'); }
        }

        function validate(step) {
            clearError(step);

            if (step === 'area') {
                var area = parseFloat(areaInput.value);
                if (isNaN(area) || area <= 0) {
                    showError('area', 'Please enter the size of your area in m².');
                    return false;
                }
                if (maxArea > 0 && area > maxArea) {
                    showError('area', 'For areas over ' + maxArea + 'm² please get in touch and we\'ll put a quote together for you.');
                    return false;
                }
                state.area = area;
                state.kitQty = Math.max(1, Math.ceil(area / coveragePerKit));
                return true;
            }

            if (step === 'colour') {
                var picked = form.querySelector('input[name="colour"]:checked');
                if (!picked) { showError('colour', 'Please choose a colour.'); return false; }
                state.colour = {
                    handle: picked.value,
                    title: picked.getAttribute('data-title'),
                    variantId: Number(picked.getAttribute('data-variant-id')) || null
                };
                return true;
            }

            var checked = stepEl(step).querySelector('input:checked');
            if (!checked) { showError(step, 'Please select an option.'); return false; }
            state[checked.name] = checked.value;
            return true;
        }

        root.querySelectorAll('[data-rkc-next]').forEach(function (btn) {
            btn.addEventListener('click', function () {
                if (!validate(current)) return;

                // Driveway is always the last question and is the branch point.
                if (current === 'driveway') {
                    if (state.driveway === 'yes') { goTo('blocked'); return; }
                    goTo('results');
                    buildResults();
                    return;
                }

                var idx = flow.indexOf(current);
                if (idx > -1 && idx + 1 < flow.length) goTo(flow[idx + 1]);
            });
        });

        root.querySelectorAll('[data-rkc-prev]').forEach(function (btn) {
            btn.addEventListener('click', function () {
                if (current === 'blocked' || current === 'results') { goTo(flow[flow.length - 1]); return; }
                var idx = flow.indexOf(current);
                if (idx > 0) goTo(flow[idx - 1]);
            });
        });

        if (restartBtn) {
            restartBtn.addEventListener('click', function () {
                form.reset();
                state = { area: 0, kitQty: 0, colour: null, mesh: 'no', glass: 'no', trowel: 'no' };
                lineItems = [];
                if (resultsWrap) resultsWrap.innerHTML = '';
                flow.forEach(clearError);
                if (cartBtn) { cartBtn.disabled = false; cartBtn.textContent = 'Add All to Basket'; }
                goTo(flow[0]);
            });
        }

        /* ------------------------------------------------------ colour swatches */

        function renderColours(products) {
            if (!coloursWrap) return;

            if (!products.length) {
                coloursWrap.innerHTML =
                    '<div class="vscx-info">We couldn\'t load the colour options just now. ' +
                    'Please <a href="/collections/' + escapeHtml(settings.kitCollection || 'easihold-pro-kits') + '">browse the kits</a> instead.</div>';
                return;
            }

            coloursWrap.innerHTML = products.map(function (p) {
                var name = shortColourName(p.title);
                var img = (p.images && p.images[0] && (p.images[0].src || p.images[0])) || '';
                var variant = (p.variants && p.variants[0]) || {};
                var available = variant.available !== false;

                return '' +
                    '<label class="vsc-option"' + (available ? '' : ' style="opacity:.45"') + '>' +
                        '<input type="radio" name="colour" value="' + escapeHtml(p.handle) + '"' +
                            ' data-title="' + escapeHtml(name) + '"' +
                            ' data-variant-id="' + escapeHtml(variant.id || '') + '"' +
                            (available ? '' : ' disabled') + ' required>' +
                        '<div class="vsc-option-inner">' +
                            '<span class="vscx-swatch">' +
                                (img ? '<img src="' + escapeHtml(img) + '" alt="' + escapeHtml(name) + '" loading="lazy">' : '') +
                            '</span>' +
                            '<span>' + escapeHtml(name) + '</span>' +
                            '<small>' + (available ? 'Covers ' + coveragePerKit + 'm² per kit' : 'Out of stock') + '</small>' +
                        '</div>' +
                    '</label>';
            }).join('');
        }

        function loadColours() {
            var prefix = settings.kitHandlePrefix || 'easihold-resin-bound-gravel-kit';

            fetchCollection(settings.kitCollection || 'easihold-pro-kits').then(function (products) {
                var kits = products.filter(function (p) {
                    return p.handle && p.handle.indexOf(prefix) === 0;
                });
                renderColours(kits);
            });
        }

        /* ----------------------------------------------------------- results */

        function glassQty() {
            var rule = (settings.glass && settings.glass.qtyRule) || 'fixed';
            if (rule === 'per_kit') return state.kitQty;
            if (rule === 'coverage') {
                var cov = Number(settings.glass.coverage) || 0;
                if (cov > 0) return Math.max(1, Math.ceil(state.area / cov));
            }
            return 1;
        }

        function meshQty() {
            var rule = (settings.mesh && settings.mesh.qtyRule) || 'per_kit';
            return rule === 'fixed' ? 1 : state.kitQty;
        }

        // Resolve every chosen product into a cart-ready line item.
        function collectItems() {
            var wanted = [];

            wanted.push({
                handle: state.colour.handle,
                qty: state.kitQty,
                label: 'Resin Bound Gravel Kit — ' + state.colour.title,
                note: 'Covers ' + (state.kitQty * coveragePerKit) + 'm²',
                tag: 'Your kit'
            });

            if (state.mesh === 'yes' && settings.mesh && settings.mesh.enabled && settings.mesh.handle) {
                wanted.push({
                    handle: settings.mesh.handle,
                    qty: meshQty(),
                    label: 'Structural Reinforcement Mesh',
                    note: 'Added strength and crack resistance',
                    tag: 'Reinforcement'
                });
            }

            if (state.glass === 'yes' && settings.glass && settings.glass.enabled && settings.glass.handle) {
                wanted.push({
                    handle: settings.glass.handle,
                    qty: glassQty(),
                    label: 'Crushed Glass',
                    note: 'For a more anti-slip finish',
                    tag: 'Anti-slip'
                });
            }

            if (state.trowel === 'yes' && settings.trowel && settings.trowel.enabled && settings.trowel.handle) {
                wanted.push({
                    handle: settings.trowel.handle,
                    qty: 1,
                    label: 'Finishing Trowel',
                    note: 'Accessory',
                    tag: 'Tool'
                });
            }

            return Promise.all(wanted.map(function (w) {
                return fetchProduct(w.handle).then(function (p) {
                    if (!p || !p.variants || !p.variants.length) return null;
                    var v = p.variants[0];
                    return {
                        handle: w.handle,
                        variantId: v.id,
                        qty: w.qty,
                        label: p.title || w.label,
                        note: w.note,
                        tag: w.tag,
                        price: typeof v.price === 'number' ? v.price : null,
                        image: p.featured_image || (p.images && p.images[0]) || null,
                        available: v.available !== false
                    };
                });
            })).then(function (items) {
                return items.filter(Boolean);
            });
        }

        function buildResults() {
            if (!resultsWrap) return;
            resultsWrap.innerHTML =
                '<div class="vscx-loading"><span class="vsc-spinner vsc-spinner--dark"></span> Building your kit list…</div>';

            collectItems().then(function (items) {
                lineItems = items;

                if (!items.length) {
                    resultsWrap.innerHTML =
                        '<h2 class="vsc-title">Something went wrong</h2>' +
                        '<div class="vscx-warning">We couldn\'t load the products just now. Please refresh and try again.</div>';
                    if (cartBtn) cartBtn.disabled = true;
                    return;
                }

                var html = '' +
                    '<h2 class="vsc-title">Your recommendation</h2>' +
                    '<p class="vsc-subtitle">Based on <strong>' + state.area + 'm²</strong> you\'ll need <strong>' +
                        state.kitQty + ' kit' + (state.kitQty > 1 ? 's' : '') + '</strong>.</p>';

                items.forEach(function (it) {
                    html += '' +
                        '<div class="vsc-result-card">' +
                            '<div class="vsc-result-media">' +
                                (it.image
                                    ? '<img src="' + escapeHtml(it.image) + '" alt="' + escapeHtml(it.label) + '">'
                                    : '<div class="vsc-no-image">No image</div>') +
                            '</div>' +
                            '<div class="vsc-result-info">' +
                                (it.tag ? '<span class="vscx-tag">' + escapeHtml(it.tag) + '</span>' : '') +
                                '<h3>' + escapeHtml(it.label) + '</h3>' +
                                '<p>' + it.qty + ' ' + (it.qty > 1 ? 'units' : 'unit') + '</p>' +
                                '<small>' + escapeHtml(it.note) + '</small>' +
                                (showPrices && it.price != null
                                    ? '<span class="vscx-result-price">' + money(it.price * it.qty) + '</span>'
                                    : '') +
                            '</div>' +
                        '</div>';
                });

                if (showPrices && items.every(function (i) { return i.price != null; })) {
                    var total = items.reduce(function (sum, i) { return sum + i.price * i.qty; }, 0);
                    html += '<div class="vscx-summary">';
                    items.forEach(function (i) {
                        html += '<div class="vscx-summary-row"><span>' + i.qty + ' × ' + escapeHtml(i.label) +
                                '</span><span>' + money(i.price * i.qty) + '</span></div>';
                    });
                    html += '<div class="vscx-summary-row vscx-summary-row--total"><span>Total</span><span>' +
                            money(total) + '</span></div>';
                    html += '</div>';
                }

                var soldOut = items.filter(function (i) { return !i.available; });
                if (soldOut.length) {
                    html += '<div class="vscx-warning">' +
                        escapeHtml(soldOut.map(function (i) { return i.label; }).join(', ')) +
                        ' is currently out of stock, so it can\'t be added to your basket.</div>';
                }

                resultsWrap.innerHTML = html;
            });
        }

        /* ---------------------------------------------------------- add to cart */

        if (cartBtn) {
            cartBtn.addEventListener('click', function () {
                if (!lineItems.length) return;

                var addable = lineItems.filter(function (i) { return i.available && i.variantId; });
                if (!addable.length) {
                    window.alert('These products are currently unavailable.');
                    return;
                }

                cartBtn.disabled = true;
                cartBtn.textContent = 'Adding…';

                fetch('/cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                    body: JSON.stringify({
                        items: addable.map(function (i) {
                            return {
                                id: i.variantId,
                                quantity: i.qty,
                                properties: { 'Calculated for': state.area + 'm²' }
                            };
                        })
                    })
                }).then(function (res) {
                    if (!res.ok) throw new Error('cart');
                    if (settings.cartRedirect === false) {
                        cartBtn.textContent = 'Added to basket ✓';
                        document.dispatchEvent(new CustomEvent('rkc:added'));
                    } else {
                        window.location.href = '/cart';
                    }
                }).catch(function () {
                    window.alert('Sorry, we couldn\'t add those to your basket. Please try again.');
                    cartBtn.disabled = false;
                    cartBtn.textContent = 'Add All to Basket';
                });
            });
        }

        /* -------------------------------------------------------------- start */

        loadColours();
        goTo(flow[0]);
    }

    function boot() {
        document.querySelectorAll('[data-rkc-section]').forEach(initSection);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }

    // Theme editor: re-init when the section is re-rendered.
    document.addEventListener('shopify:section:load', function (e) {
        var el = e.target.querySelector ? e.target.querySelector('[data-rkc-section]') : null;
        if (el) initSection(el);
    });

})();
